import {
  ROLE_ADMIN,
  ROLE_AGENT,
  ROLE_MANAGER,
  ROLE_MODERATOR,
  ROLE_SUPER_ADMIN,
} from "./constants";
import {ComponentType, FC, lazy} from "react";
import AdminPanelOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import Edit from "@mui/icons-material/EditNoteOutlined";
import EventOutlined from "@mui/icons-material/EventOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import NotificationsOutlined from "@mui/icons-material/NotificationsOutlined";
import OfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import PhoneCallback from "@mui/icons-material/PhoneCallback";
import ReportOutlined from "@mui/icons-material/SummarizeOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import SmsOutlined from "@mui/icons-material/SmsOutlined";
import TransactionOutlined from "@mui/icons-material/ReceiptOutlined";
import TaxOutlined from "@mui/icons-material/CalculateOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import User360Outlined from "@mui/icons-material/PersonSearchOutlined";
import WebOutlined from "@mui/icons-material/WebOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaymentsIcon from "@mui/icons-material/Payments";
import {Role} from "types/global";

export const DashboardWrapperPage = lazy(
  () => import("layouts/DashboardLayout"),
);
const Dashboard = lazy(() => import("pages/dashboard"));
const CampaignCallsPage = lazy(() => import("pages/campaigns/calls"));
const CampaignReportPage = lazy(() => import("pages/campaigns/reports"));
const ManageCampaignsPage = lazy(() => import("pages/campaigns/manage"));
const InsightsPage = lazy(() => import("pages/insights/InsightsPage"));
const ChatPage = lazy(() => import("pages/chat/ChatPage"));
const ModeratorsPage = lazy(() => import("pages/moderators/ModeratorsPage"));
const OffersPage = lazy(() => import("pages/offers/OffersPage"));
const ServiceSettings = lazy(() => import("pages/settings"));
const TaxPage = lazy(() => import("pages/taxes"));
const TransactionVerificationPage = lazy(
  () => import("pages/transaction/TransactionVerificationPage"),
);
const User360Page = lazy(() => import("pages/User360/User360Page"));
const WebPageContentsPage = lazy(() => import("pages/web-pages"));
const BulkSMSNotificationsPage = lazy(
  () => import("pages/notifications/bulk-sms"),
);
const BulkSMSNotificationsHistoryPage = lazy(
  () => import("pages/notifications/bulk-sms-history"),
);
const SendSMSNotificationsPage = lazy(
  () => import("pages/notifications/send-sms"),
);
const NotificationMessageConfigurationPage = lazy(
  () => import("pages/notifications/message-configuration"),
);
const DrawsPage = lazy(() => import("pages/draws"));

const LeadCallsPage = lazy(() => import("pages/leads/calls"));
const LeadReportPage = lazy(() => import("pages/leads/reports"));

const ManageLeadsPage = lazy(() => import("pages/leads/manage"));
const BrandsPage = lazy(() => import("pages/brands"));
const Games = lazy(() => import("pages/games"));

const PushNotifications = lazy(
  () => import("pages/notifications/push-notifications"),
);

const WithdrawDeactivationsPage = lazy(
  () => import("pages/withdraw-deactivations"),
);

const PaymentGatewayStatsPage = lazy(
  () => import("src/pages/payment-gateways"),
);

export type Page = {
  segment: string;
  title: string;
  Icon: ComponentType<any>;
  Component: FC;
  minRole: Role;
  country?: string;
};

export type PageGroup = Omit<Page, "Component" | "minRole" | "country"> & {
  children: Array<Page | PageGroup>;
};

const pages: Array<Page | PageGroup> = [
  {
    segment: "dash",
    title: "Dashboard",
    Icon: DashboardOutlined,
    Component: Dashboard,
    minRole: ROLE_MANAGER,
  },
  {
    segment: "insights",
    title: "Insights",
    Icon: InsightsOutlined,
    Component: InsightsPage,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "settings",
    title: "Settings",
    Icon: SettingsOutlined,
    Component: ServiceSettings,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "notifications",
    title: "Notifications",
    Icon: NotificationsOutlined,
    children: [
      {
        segment: "send-sms",
        title: "Send SMS",
        Icon: SmsOutlined,
        Component: SendSMSNotificationsPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "message-configuration",
        title: "Message Configuration",
        Icon: TuneOutlined,
        Component: NotificationMessageConfigurationPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "bulk-sms",
        title: "Bulk SMS",
        Icon: ReportOutlined,
        Component: BulkSMSNotificationsPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "bulk-sms-history",
        title: "Bulk SMS History",
        Icon: HistoryOutlined,
        Component: BulkSMSNotificationsHistoryPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "push-notifications",
        title: "Push Notifications",
        Icon: NotificationsActiveIcon,
        minRole: ROLE_MANAGER,
        Component: PushNotifications,
      },
    ],
  },
  {
    segment: "brands",
    title: "Brands",
    Icon: BusinessIcon,
    Component: BrandsPage,
    minRole: ROLE_SUPER_ADMIN,
  },
  {
    segment: "games",
    title: "Games",
    Icon: SportsEsportsIcon,
    Component: Games,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "tax",
    title: "Taxes",
    Icon: TaxOutlined,
    Component: TaxPage,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "offers",
    title: "Offers",
    Icon: OfferOutlined,
    Component: OffersPage,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "moderators",
    title: "Moderators",
    Icon: AdminPanelOutlined,
    Component: ModeratorsPage,
    minRole: ROLE_MANAGER,
  },
  {
    segment: "pages",
    title: "Web Pages",
    Icon: WebOutlined,
    Component: WebPageContentsPage,
    minRole: ROLE_MANAGER,
  },
  {
    segment: "draws",
    title: "Draws",
    Icon: EventOutlined,
    Component: DrawsPage,
    minRole: ROLE_MANAGER,
  },
  {
    segment: "user-360",
    title: "User 360",
    Icon: User360Outlined,
    Component: User360Page,
    minRole: ROLE_AGENT,
  },
  {
    segment: "withdraw-deactivations",
    title: "Withdraw Deactivations",
    Icon: MoneyOffIcon,
    Component: WithdrawDeactivationsPage,
    minRole: ROLE_MANAGER,
  },
  {
    segment: "chat",
    title: "Chat",
    Icon: ChatOutlined,
    Component: ChatPage,
    minRole: ROLE_MODERATOR,
  },
  {
    segment: "verify",
    title: "Transactions",
    Icon: TransactionOutlined,
    Component: TransactionVerificationPage,
    country: "ke",
    minRole: ROLE_MODERATOR,
  },
  {
    segment: "payment-gateways",
    title: "Payment Gateways",
    Icon: PaymentsIcon,
    Component: PaymentGatewayStatsPage,
    minRole: ROLE_ADMIN,
  },
  {
    segment: "campaigns",
    title: "Campaigns",
    Icon: CampaignOutlined,
    children: [
      {
        segment: "manage",
        title: "Manage Campaigns",
        Icon: Edit,
        Component: ManageCampaignsPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "calls",
        title: "Calls",
        Icon: PhoneCallback,
        Component: CampaignCallsPage,
        minRole: ROLE_AGENT,
      },
      {
        segment: "reports",
        title: "Reports",
        Icon: ReportOutlined,
        Component: CampaignReportPage,
        minRole: ROLE_MANAGER,
      },
    ],
  },
  {
    segment: "leads",
    title: "Leads",
    Icon: GroupOutlined,
    children: [
      {
        segment: "manage",
        title: "Manage Leads",
        Icon: Edit,
        Component: ManageLeadsPage,
        minRole: ROLE_MANAGER,
      },
      {
        segment: "calls",
        title: "Calls",
        Icon: PhoneCallback,
        Component: LeadCallsPage,
        minRole: ROLE_AGENT,
      },
      {
        segment: "reports",
        title: "Reports",
        Icon: ReportOutlined,
        Component: LeadReportPage,
        minRole: ROLE_MANAGER,
      },
    ],
  },
];

export default pages;
